// Shared Alpaca API module — keys from localStorage, typed responses.
// fetchAlpacaNews is used by Daily Brief and News Feed.

const ALPACA_DATA_URL = 'https://data.alpaca.markets';

const HIGH_PRIORITY = ['S-1','IPO','acquisition','merger','FDA approval','clinical hold',
  'contract award','Golden Dome','earnings beat','earnings miss','guidance','bankruptcy',
  'SEC investigation','insider buying','buyout','partnership','Iran strike','Iran nuclear',
  'ceasefire','CLARITY Act','crypto bill','sanctions'];

const WATCH = ['funding','raises','valuation','phase','trial','revenue','analyst',
  'upgrade','downgrade','price target','contract','launch','satellite','patent',
  'collaboration','Iran','Middle East','CLARITY','crypto regulation','digital assets',
  'SEC CFTC'];

export function flagArticle(title: string): string {
  const t = title.toLowerCase();
  if (HIGH_PRIORITY.some(k => t.includes(k.toLowerCase()))) return '🔴';
  if (WATCH.some(k => t.includes(k.toLowerCase()))) return '🟡';
  return '⚪';
}

export interface AlpacaArticle {
  ticker:      string;
  source:      string;
  title:       string;
  link:        string;
  date:        string;
  description: string;
  flag:        string;
}

function getKeys(): { key: string; secret: string } {
  return {
    key:    localStorage.getItem('meridian_alpaca_key')    ?? '',
    secret: localStorage.getItem('meridian_alpaca_secret') ?? '',
  };
}

export interface PriceData {
  price:     number;
  change:    number;
  changePct: number;
}

const ALPACA_PAPER = 'https://paper-api.alpaca.markets';
const ALPACA_DATA  = 'https://data.alpaca.markets';

export async function alpacaFetch<T = unknown>(
  endpoint: string,
  method:  'GET' | 'POST' | 'DELETE' = 'GET',
  body:    unknown = null,
  isData = false,
): Promise<T> {
  const { key, secret } = getKeys();
  if (!key || !secret) throw new Error('Alpaca keys not configured');
  const base = isData ? ALPACA_DATA : ALPACA_PAPER;
  const opts: RequestInit = {
    method,
    headers: {
      'APCA-API-KEY-ID': key,
      'APCA-API-SECRET-KEY': secret,
      // Content-Type only when there's a body: on GETs it forces a CORS
      // preflight that data.alpaca.markets rejects ("Failed to fetch").
      // paper-api tolerates it, which is why this hid until the gauges.
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    signal: AbortSignal.timeout(10000),
  };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(base + endpoint, opts);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText })) as { message?: string };
    throw new Error(err.message ?? `HTTP ${res.status}`);
  }
  return (res.status === 204 ? null : res.json()) as Promise<T>;
}

export async function fetchAlpacaSnapshot(ticker: string): Promise<PriceData | null> {
  const { key, secret } = getKeys();
  if (!key || !secret) return null;
  try {
    const res = await fetch(
      `${ALPACA_DATA_URL}/v2/stocks/${ticker}/snapshot?feed=iex`,
      { headers: { 'APCA-API-KEY-ID': key, 'APCA-API-SECRET-KEY': secret }, signal: AbortSignal.timeout(5000) },
    );
    if (!res.ok) return null;
    const d = await res.json() as any;
    const price = d?.latestTrade?.p as number | undefined;
    const prev  = d?.prevDailyBar?.c as number | undefined;
    if (!price) return null;
    const change    = prev ? price - prev : 0;
    const changePct = prev ? (change / prev) * 100 : 0;
    return { price, change, changePct };
  } catch {
    return null;
  }
}

export async function fetchAlpacaNews(
  tickers: string[],
  limit = 50,
): Promise<AlpacaArticle[] | null> {
  const { key, secret } = getKeys();
  if (!key || !secret) return null;

  try {
    const url = `${ALPACA_DATA_URL}/v1beta1/news?symbols=${encodeURIComponent(tickers.join(','))}&limit=${limit}&sort=desc&include_content=false`;
    const res = await fetch(url, {
      headers: { 'APCA-API-KEY-ID': key, 'APCA-API-SECRET-KEY': secret },
      signal: AbortSignal.timeout(8000),
    });
    if (!res.ok) return null;
    const data = await res.json() as { news?: any[] };
    return (data.news ?? []).map(item => ({
      ticker:      (item.symbols?.[0] as string | undefined) ?? 'GENERAL',
      source:      `Alpaca · ${(item.source as string | undefined) ?? 'Benzinga'}`,
      title:       (item.headline as string | undefined) ?? 'No title',
      link:        (item.url as string | undefined) ?? '#',
      date:        (item.created_at as string | undefined) ?? (item.updated_at as string | undefined) ?? '',
      description: item.summary ? String(item.summary).substring(0, 160) + '…' : '',
      flag:        flagArticle((item.headline as string | undefined) ?? ''),
    }));
  } catch {
    return null;
  }
}
